import { useFormatters } from '@/hooks/useFormatters';
import ECharts from '@features/echarts';
import { plural } from '@lingui/macro';
import { EChartsOption } from 'echarts';
import { FC, useMemo } from 'react';
import { HeatmapData } from '../types';
import { Box } from '@mui/material';

interface Props {
  data: HeatmapData;
}

export const Heatmap: FC<Props> = ({ data }) => {
  const { formatDate } = useFormatters();

  const { length, option } = useMemo(() => {
    const currentYear = new Date().getFullYear();

    const years: number[] = [];

    for (let y = currentYear; years.length < 5 && y >= 2023; y--) {
      years.push(y);
    }

    const series = years.map((year) => {
      const points = data.filter((point) => {
        const date = new Date(point[0]);
        return date.getFullYear() === year;
      });

      const byDay: Record<string, number | undefined> = {};

      points.forEach((point) => {
        const pad = (n: number) => n.toString().padStart(2, '0');
        const date = new Date(point[0]);
        const year = date.getFullYear();
        const month = pad(date.getMonth());
        const day = pad(date.getDate());

        const dateString = `${year}-${month}-${day}`;

        const count = byDay[dateString] ?? 0;
        byDay[dateString] = count + 1;
      });

      return Object.entries(byDay);
    });

    const option: EChartsOption = {
      legend: {
        show: false,
      },
      tooltip: {
        trigger: 'item',
        padding: 0,
        backgroundColor: 'transparent',
        borderWidth: 0,
        renderMode: 'html',
        show: true,
        confine: true,
        formatter(params) {
          if (typeof params === 'object' && !Array.isArray(params)) {
            const marker = params.marker;
            const value = params.value as [number, number];

            const datetime = formatDate(new Date(value[0]));
            const count = plural(value[1], {
              one: '1 acceso',
              other: '# accesos',
            });

            return `<div 
  style="color: #fff; line-height: 1.5; background-color: rgba(97, 97, 97, 0.92); font-size: 13px; padding: 4px 8px; border-radius: 4px">
  ${datetime}</br>
${marker}<b style="font-size: 14px">${count}</b>
</div>`;
          }
          return '';
        },
      },
      visualMap: {
        show: false,
        type: 'piecewise',
        dimension: 1,
        pieces: [
          {
            color: 'white',
          },
        ],
      },
      calendar: years.map((year, i) => {
        return {
          top: `${20 + i * 130}`,
          left: 50,
          right: 30,
          cellSize: ['auto', 13],
          range: String(year),
          itemStyle: {
            color: '#303b49',
            borderColor: 'rgba(0, 0, 0, 0.3)',
          },
          yearLabel: {
            show: true,
            top: 50,
            left: 50,
            color: 'white',
          },
          dayLabel: {
            color: 'white',
            show: true,
            firstDay: 1,
            margin: 10,
          },
          monthLabel: {
            show: true,
            color: 'white',
            margin: 10,
          },
          splitLine: {
            lineStyle: {
              width: 2,
            },
          },
        };
      }),
      series: series.map((data, i) => ({
        type: 'heatmap',
        coordinateSystem: 'calendar',
        data: data,
        calendarIndex: i,
        color: 'white',
      })),
    };

    return {
      option,
      length: years.length,
    };
  }, [data, formatDate]);

  return (
    <Box height={length * 135}>
      <ECharts option={option} />
    </Box>
  );
};
